import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, assetUrl, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const Tile = ({ selected, onClick, testId, title, label, style, busy }) => (
  <button type="button" onClick={onClick} data-testid={testId} title={title} aria-pressed={selected}
    className={`group flex flex-col items-center gap-1.5 rounded-2xl p-1 transition-transform duration-200 hover:-translate-y-0.5 ${busy ? "opacity-60" : ""}`}>
    <span className={`flex h-12 w-12 items-center justify-center rounded-xl border bg-cover bg-center ${selected ? "ring-2 ring-slate-900 ring-offset-2" : "border-slate-200"}`} style={style}>
      {selected && <Check className="h-5 w-5 drop-shadow" />}
    </span>
    <span className="text-[10px] font-semibold text-slate-600">{label}</span>
  </button>
);

export const ThemePicker = ({ current, useImage, imageUrl, themes, onChange }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);

  const save = async (body) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/me/theme", body);
      onChange(data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const image = useImage && !!imageUrl;
  const th = themes?.[current];

  return (
    <div className="rounded-3xl border border-slate-200/80 bg-white p-5" data-testid="theme-picker">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("card_colour")}</p>
      <p className="mt-1 text-xs text-slate-400">{t("card_colour_hint")}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        {imageUrl && (
          <Tile selected={image} busy={busy} onClick={() => !image && save({ bg_image: true })} testId="theme-option-image"
            title={t("Background image")} label={t("Image")} style={{ backgroundImage: `url("${assetUrl(imageUrl)}")`, color: "#fff" }} />
        )}
        {Object.entries(themes || {}).map(([key, opt]) => (
          <Tile key={key} selected={!image && key === current} busy={busy} onClick={() => (image || key !== current) && save({ theme: key, bg_image: false })}
            testId={`theme-option-${key}`} title={opt.name} label={opt.name} style={{ backgroundColor: opt.bg, color: opt.fg }} />
        ))}
      </div>
      {image && th && (
        <p className="mt-3 text-xs text-slate-400" data-testid="theme-image-note">{t("The image blends into your current colour: {name}", { name: th.name })}</p>
      )}
    </div>
  );
};
